/**
 * Draft Creation Step
 * First step shown when creating a new application.
 * Captures borrower name, email, and phone with no autosave.
 * On manual submit, calls the create-draft endpoint, then transitions to the full DGA.
 */

import * as React from "react";
import { Input, PhoneInput, Button, FileAddIcon } from "@porchpass/ui";
import { useDgaApi } from "../hooks/useDgaApi";
import CreateApplicationDialog from "../../DuplicateBorrowerConfirmationDialog";
import {
  draftCreationFormSchema,
  emailSchema,
} from "../utils/validationSchemas";
import {
  isDuplicateCoborrowerConfirmableError,
  isDuplicatePrimaryBorrowerError,
} from "src/utils/duplicateBorrowerErrors";

interface DraftCreationStepProps {
  onDraftCreated: (
    applicationId: string,
    borrowerId: string,
    formData: {
      firstName: string;
      lastName: string;
      email: string;
      phone: string;
    },
  ) => void;
}

const DraftCreationStep: React.FC<DraftCreationStepProps> = ({
  onDraftCreated,
}) => {
  const api = useDgaApi();

  const [firstName, setFirstName] = React.useState("");
  const [lastName, setLastName] = React.useState("");
  const [email, setEmail] = React.useState("");
  const [phone, setPhone] = React.useState("");
  const [fieldErrors, setFieldErrors] = React.useState<Record<string, string>>(
    {},
  );
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [submitError, setSubmitError] = React.useState<string | null>(null);
  const [isDialogOpen, setIsDialogOpen] = React.useState(false);
  const [pendingFormData, setPendingFormData] = React.useState<{
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
  } | null>(null);

  const clearFieldError = (field: string) => {
    if (fieldErrors[field]) {
      setFieldErrors((prev) => {
        const { [field]: _, ...next } = prev;
        return next;
      });
    }
    if (submitError) setSubmitError(null);
  };

  const handleDialogConfirm = async () => {
    if (!pendingFormData) return;

    try {
      const resp = await api.createDraftApplicationIgnoreDuplicate(
        pendingFormData.firstName,
        pendingFormData.lastName,
        pendingFormData.email || undefined,
        pendingFormData.phone || undefined,
      );
      if (resp.status === 200 && resp.data) {
        const respData = resp.data;
        if (respData.application_id && respData.borrower_id) {
          onDraftCreated(
            respData.application_id,
            respData.borrower_id,
            pendingFormData,
          );
        } else {
          console.error(
            "Draft creation response missing required fields",
            respData,
          );
          throw new Error("Something went wrong. Please try again.");
        }
      } else {
        console.error("Unexpected draft creation response", resp);
        throw new Error("Something went wrong. Please try again.");
      }
    } catch (err) {
      const errorMsg =
        err instanceof Error
          ? err.message
          : "Something went wrong. Please try again.";
      setSubmitError(errorMsg);
      throw err;
    }
  };

  const handleEmailBlur = () => {
    if (!email) return;
    if (!emailSchema.safeParse(email).success) {
      setFieldErrors((prev) => ({
        ...prev,
        email: "Invalid email address",
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);
    setFieldErrors({});

    const result = draftCreationFormSchema.safeParse({
      firstName,
      lastName,
      email,
      phoneNumber: phone,
    });

    if (!result.success) {
      // Keep only the first error per field (.and() can produce duplicates)
      const errors: Record<string, string> = {};
      for (const issue of result.error.issues) {
        const field = String(issue.path[0]);
        if (!errors[field]) {
          errors[field] = issue.message;
        }
      }
      setFieldErrors(errors);
      return;
    }

    setIsSubmitting(true);
    try {
      const resp = await api.createDraftApplication(
        firstName,
        lastName,
        email || undefined,
        phone || undefined,
      );
      if (resp.status === 200 && resp.data) {
        const respData = resp.data;
        if (respData.application_id && respData.borrower_id) {
          onDraftCreated(respData.application_id, respData.borrower_id, {
            firstName,
            lastName,
            email,
            phone,
          });
        } else {
          // 200 response but missing required fields; treat as an error
          console.error(
            "Draft creation response missing required fields",
            respData,
          );
          setSubmitError("Something went wrong. Please try again.");
        }
      } else {
        // Non-200 status or missing data; treat as an error
        console.error("Unexpected draft creation response", resp);
        setSubmitError("Something went wrong. Please try again.");
      }
    } catch (err) {
      const status =
        err && typeof err === "object" && "status" in err
          ? (err as { status: number }).status
          : undefined;
      const message = err instanceof Error ? err.message : "";
      if (status === 400 && isDuplicatePrimaryBorrowerError(message)) {
        const errorMsg =
          "Already associated with another application's primary borrower";
        setFieldErrors((prev) => ({
          ...prev,
          email: errorMsg,
          phoneNumber: errorMsg,
        }));
      } else if (
        status === 400 &&
        isDuplicateCoborrowerConfirmableError(message)
      ) {
        setPendingFormData({ firstName, lastName, email, phone });
        setIsDialogOpen(true);
      } else {
        setSubmitError("Something went wrong. Please try again.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const hasErrors = Object.keys(fieldErrors).length > 0;

  return (
    <div className="flex flex-1 items-center justify-center">
      <div className="w-full max-w-[480px]">
        <div className="flex flex-col items-center mb-10">
          <h2 className="flex items-center gap-1 text-[28px] font-semibold leading-[32px] tracking-[-0.168px] text-[#111827]">
            New application{" "}
            <span className="inline-flex items-center justify-center px-1.5 py-1 bg-[#e5ebfd] rounded-[24px]">
              <FileAddIcon
                variant="bulk-rounded"
                size={18}
                className="text-[#3265ea]"
              />
            </span>
          </h2>
          <p className="text-sm text-[#6b7280] mt-2">
            Enter the borrower&apos;s information to get started.
          </p>
        </div>

        <form onSubmit={(e) => void handleSubmit(e)} className="space-y-3">
          <Input
            label="First name"
            size="s"
            value={firstName}
            error={!!fieldErrors.firstName}
            helperText={fieldErrors.firstName}
            onChange={(e) => {
              setFirstName(e.target.value);
              clearFieldError("firstName");
            }}
          />
          <Input
            label="Last name"
            size="s"
            value={lastName}
            error={!!fieldErrors.lastName}
            helperText={fieldErrors.lastName}
            onChange={(e) => {
              setLastName(e.target.value);
              clearFieldError("lastName");
            }}
          />
          <Input
            label="Email address"
            type="email"
            size="s"
            value={email}
            error={!!fieldErrors.email}
            helperText={fieldErrors.email}
            onChange={(e) => {
              setEmail(e.target.value);
              clearFieldError("email");
            }}
            onBlur={handleEmailBlur}
          />
          <PhoneInput
            label="Phone number"
            size="s"
            value={phone}
            error={!!fieldErrors.phoneNumber}
            helperText={fieldErrors.phoneNumber}
            onChange={(value) => {
              setPhone(value);
              clearFieldError("phoneNumber");
            }}
          />

          {submitError && (
            <p className="text-sm text-[#872525]">{submitError}</p>
          )}

          <div className="pt-2">
            <Button
              variant="primary"
              type="submit"
              loading={isSubmitting}
              disabled={isSubmitting || hasErrors}
              className="w-full"
            >
              Create Application
            </Button>
          </div>
        </form>
      </div>

      <CreateApplicationDialog
        open={isDialogOpen}
        onOpenChange={setIsDialogOpen}
        borrowerName={`${firstName} ${lastName}`}
        onConfirm={handleDialogConfirm}
      />
    </div>
  );
};

export default DraftCreationStep;
